/* STRAINLAB theme JS — vanilla, progressive enhancement only.
   Every interactive component is a custom element so it survives
   Theme Editor section reloads without global re-initialisation. */
(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ utils */
  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');

  /* localStorage throws on access inside the Theme Editor iframe (third-
     party storage blocked) and in some private modes — never let a
     convenience preference take a whole component down. */
  const storage = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch {
        /* ignore */
      }
    },
  };

  let toastEl = null;
  let toastTimer = null;
  const toast = (message) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'sl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.innerHTML = '<i class="fa-solid fa-circle-check" aria-hidden="true"></i><span></span>';
      document.body.appendChild(toastEl);
    }
    toastEl.querySelector('span').textContent = message;
    requestAnimationFrame(() => toastEl.classList.add('on'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('on'), 3200);
  };

  /* ------------------------------------------------------- cart client */
  const routes = window.SLTheme?.routes || {};
  const strings = window.SLTheme?.strings || {};

  const publishCart = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  /* Parse defensively: throttling (429), server errors and the password
     page answer with HTML, and response.json() would surface a raw
     SyntaxError to the customer. */
  const readJSON = async (response) => {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  const cartRequest = async (endpoint, payload) => {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await readJSON(response);
    if (!response.ok || !data) {
      throw new Error(data?.description || data?.message || strings.cart_error || 'Cart error');
    }
    return data;
  };

  const SLCart = {
    async add(items, sections) {
      const data = await cartRequest(routes.cart_add_url + '.js', { items, sections });
      const cartResponse = await fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
      const cart = cartResponse.ok ? await readJSON(cartResponse) : null;
      if (cart) publishCart(cart);
      return data;
    },
    async change(payload) {
      const data = await cartRequest(routes.cart_change_url + '.js', payload);
      publishCart(data);
      return data;
    },
    async update(payload) {
      const data = await cartRequest(routes.cart_update_url + '.js', payload);
      publishCart(data);
      return data;
    },
  };
  window.SLCart = SLCart;

  /* --------------------------------------------- reveal + parallax */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  const parallaxEls = new Set();
  let parallaxTicking = false;
  const runParallax = () => {
    const vh = innerHeight;
    // Read all layout first, then write — interleaving forces a reflow
    // per element on every frame.
    const reads = [];
    parallaxEls.forEach((el) => {
      if (!el.isConnected) {
        parallaxEls.delete(el);
        return;
      }
      reads.push([el, el.getBoundingClientRect()]);
    });
    reads.forEach(([el, rect]) => {
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      const factor = parseFloat(el.dataset.px) || 0.05;
      el.style.transform = `translate3d(0,${(-progress * factor * vh).toFixed(1)}px,0)`;
    });
    parallaxTicking = false;
  };

  addEventListener(
    'scroll',
    () => {
      if (reducedMotion.matches || parallaxEls.size === 0 || parallaxTicking) return;
      parallaxTicking = true;
      requestAnimationFrame(runParallax);
    },
    { passive: true }
  );

  const initMotion = (root) => {
    root.querySelectorAll('.rv:not(.in)').forEach((el) => revealObserver.observe(el));
    if (!reducedMotion.matches) {
      root.querySelectorAll('.px').forEach((el) => parallaxEls.add(el));
      runParallax();
    }
  };

  initMotion(document);
  document.addEventListener('shopify:section:load', (event) => initMotion(event.target));

  /* -------------------------------------------------- <sl-header> */
  customElements.define(
    'sl-header',
    class extends HTMLElement {
      connectedCallback() {
        this.drawer = this.querySelector('[data-drawer]');
        this.drawerToggle = this.querySelector('[data-drawer-toggle]');
        this.searchDialog = this.querySelector('dialog[data-search]');

        this.drawerToggle?.addEventListener('click', () => this.toggleDrawer());
        this.querySelectorAll('[data-search-open]').forEach((btn) =>
          btn.addEventListener('click', () => this.openSearch())
        );
        this.searchDialog?.addEventListener('click', (event) => {
          if (event.target === this.searchDialog) this.searchDialog.close();
        });
        this.searchDialog
          ?.querySelector('[data-search-close]')
          ?.addEventListener('click', () => this.searchDialog.close());
        this.onKeydown = (event) => {
          if (event.key === 'Escape' && this.drawer && !this.drawer.hidden) this.toggleDrawer(false);
        };
        addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        removeEventListener('keydown', this.onKeydown);
        // An editor reload while the drawer is open must not leave the
        // page scroll-locked or the main content inert.
        document.documentElement.style.overflow = '';
        document.getElementById('main-content')?.removeAttribute('inert');
      }

      toggleDrawer(force) {
        if (!this.drawer) return;
        const open = force !== undefined ? force : this.drawer.hidden;
        this.drawer.hidden = !open;
        this.drawerToggle.setAttribute('aria-expanded', open);
        document.documentElement.style.overflow = open ? 'hidden' : '';
        // Keep keyboard/screen-reader focus inside the drawer while open.
        const main = document.getElementById('main-content');
        if (main) main.toggleAttribute('inert', open);
        if (open) this.drawer.querySelector('a, button, summary')?.focus();
        else this.drawerToggle.focus();
      }

      openSearch() {
        if (!this.searchDialog) return;
        this.searchDialog.showModal();
        this.searchDialog.querySelector('input[type="search"]')?.focus();
      }
    }
  );

  /* --------------------------------------- <sl-predictive-search> */
  customElements.define(
    'sl-predictive-search',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-results]');
        this.sectionId = this.dataset.sectionId;
        if (!this.input || !this.results) return;
        this.input.addEventListener(
          'input',
          debounce(() => this.search(), 250)
        );
      }

      async search() {
        const query = this.input.value.trim();
        if (query.length < 2) {
          this.results.innerHTML = '';
          this.results.hidden = true;
          return;
        }
        try {
          const url = `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=6&section_id=${this.sectionId}`;
          const response = await fetch(url);
          if (!response.ok) return;
          const doc = parseHTML(await response.text());
          const inner = doc.querySelector('[data-results]');
          if (inner && this.input.value.trim() === query) {
            this.results.innerHTML = inner.innerHTML;
            this.results.hidden = false;
          }
        } catch {
          /* predictive search is an enhancement — fail silently */
        }
      }
    }
  );

  /* ----------------------------------------------- <sl-quantity> */
  customElements.define(
    'sl-quantity',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button[data-step]');
          if (!button || !this.input) return;
          event.preventDefault();
          const step = parseInt(button.dataset.step, 10);
          const parsedMin = parseInt(this.input.min, 10);
          const min = Number.isNaN(parsedMin) ? 1 : parsedMin;
          const max = parseInt(this.input.max, 10) || 9999;
          const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
          if (next !== parseInt(this.input.value, 10)) {
            this.input.value = next;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }
  );

  /* ------------------------------------------- <sl-product-form> */
  customElements.define(
    'sl-product-form',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        const dataEl = this.querySelector('[data-product-json]');
        if (!this.form || !dataEl) return;
        this.product = JSON.parse(dataEl.textContent);
        this.idInput = this.form.querySelector('input[name="id"]');
        this.submitButton = this.form.querySelector('[data-atc]');
        this.optionInputs = () => [...this.querySelectorAll('input[data-option-position]')];

        this.addEventListener('change', (event) => {
          if (event.target.matches('input[data-option-position]')) this.onOptionChange();
        });
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.onOptionChange(true);
      }

      selectedOptions() {
        const options = [];
        this.optionInputs().forEach((input) => {
          if (input.checked) options[parseInt(input.dataset.optionPosition, 10) - 1] = input.value;
        });
        return options;
      }

      currentVariant() {
        // No option inputs (default-variant-only products): keep the
        // server-selected variant instead of matching against options.
        if (this.optionInputs().length === 0) {
          const currentId = parseInt(this.idInput?.value, 10);
          return this.product.variants.find((variant) => variant.id === currentId) || this.product.variants[0];
        }
        const selected = this.selectedOptions();
        return this.product.variants.find((variant) =>
          variant.options.every((value, index) => value === selected[index])
        );
      }

      onOptionChange(initial) {
        const variant = this.currentVariant();
        this.updateAvailabilityHints();
        this.updateLabels();
        const detail = { variant, product: this.product, initial: !!initial };
        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail }));
        this.syncInfo(variant);

        if (!variant) {
          this.setButton(false, strings.unavailable);
          return;
        }
        this.idInput.value = variant.id;
        if (!initial) {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
        this.setButton(variant.available, variant.available ? null : strings.sold_out);
      }

      /* Update price, stock line and the sticky bar for the new variant. */
      syncInfo(variant) {
        const scope = this.closest('.shopify-section') || document;
        if (!variant) return;

        const priceNow = scope.querySelector('[data-price-now]');
        const priceWas = scope.querySelector('[data-price-was]');
        if (priceNow) priceNow.textContent = variant.price;
        if (priceWas) {
          priceWas.hidden = !variant.compare_at;
          priceWas.textContent = variant.compare_at || '';
        }

        const stock = scope.querySelector('[data-stock]');
        if (stock) {
          const dot = stock.querySelector('[data-stock-dot]');
          const text = stock.querySelector('[data-stock-text]');
          const threshold = parseInt(stock.dataset.lowThreshold, 10) || 0;
          let state = 'ok';
          let label = stock.dataset.note || strings.in_stock;
          if (!variant.available) {
            state = 'off';
            label = strings.sold_out;
          } else if (variant.qty !== null && variant.qty > 0 && variant.qty <= threshold) {
            state = 'low';
            label = strings.low_stock.replace('[count]', variant.qty);
          }
          dot?.classList.toggle('slow', state === 'low');
          dot?.classList.toggle('off', state === 'off');
          if (text) text.textContent = label;
        }

        const stickyPrice = scope.querySelector('[data-sticky-price]');
        const stickyVariant = scope.querySelector('[data-sticky-variant]');
        if (stickyPrice) stickyPrice.textContent = variant.price;
        if (stickyVariant && variant.options.length) stickyVariant.textContent = this.selectedOptions().join(' · ');
        const stickyButton = scope.querySelector('[data-sticky-atc]');
        stickyButton?.toggleAttribute('disabled', !variant.available);
      }

      setButton(enabled, label) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('disabled', !enabled);
        const textEl = this.submitButton.querySelector('[data-atc-text]') || this.submitButton;
        if (!this.defaultLabel) this.defaultLabel = textEl.dataset.defaultLabel || textEl.textContent.trim();
        textEl.textContent = label || this.defaultLabel;
      }

      /* Mark option values that have no available variant given the other
         currently selected options. */
      updateAvailabilityHints() {
        const selected = this.selectedOptions();
        this.optionInputs().forEach((input) => {
          const position = parseInt(input.dataset.optionPosition, 10) - 1;
          const candidate = selected.slice();
          candidate[position] = input.value;
          const match = this.product.variants.find((variant) =>
            variant.options.every((value, index) => value === candidate[index])
          );
          input.closest('[data-option-value]')?.classList.toggle('is-unavailable', !match || !match.available);
        });
      }

      updateLabels() {
        this.querySelectorAll('[data-option-label]').forEach((label) => {
          const position = parseInt(label.dataset.optionLabel, 10) - 1;
          const value = this.selectedOptions()[position];
          if (value) label.textContent = value;
        });
      }

      async onSubmit(event) {
        if (this.dataset.ajax !== 'true') return;
        event.preventDefault();
        if (this.submitButton?.hasAttribute('disabled')) return;
        this.submitButton?.classList.add('is-loading');
        try {
          const formData = new FormData(this.form);
          await SLCart.add([
            {
              id: parseInt(formData.get('id'), 10),
              quantity: parseInt(formData.get('quantity'), 10) || 1,
            },
          ]);
          toast(strings.added_to_cart);
        } catch (error) {
          toast(error.message);
        } finally {
          this.submitButton?.classList.remove('is-loading');
        }
      }
    }
  );

  /* ---------------------------------------- <sl-product-gallery> */
  customElements.define(
    'sl-product-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-gallery-main]');
        this.zoom = this.querySelector('[data-gallery-zoom]');
        this.addEventListener('click', (event) => {
          const thumb = event.target.closest('button[data-media-id]');
          if (thumb) return this.show(thumb.dataset.mediaId);
          if (!this.zoom) return;
          const open = event.target.closest('[data-zoom-open]');
          if (open) return this.openZoom(open.closest('[data-gallery-item]')?.dataset.galleryItem);
          if (event.target.closest('[data-zoom-close]')) return this.zoom.close();
          if (event.target.closest('[data-zoom-prev]')) return this.stepZoom(-1);
          if (event.target.closest('[data-zoom-next]')) return this.stepZoom(1);
          const zoomThumb = event.target.closest('button[data-zoom-thumb]');
          if (zoomThumb) return this.showZoom(zoomThumb.dataset.zoomThumb);
          // The img fills its frame with object-fit: contain, so decide via
          // geometry whether the click hit the photo or the letterbox around it.
          const zoomImg = event.target.closest('.gal-zoom__img');
          if (zoomImg) {
            if (!zoomImg.naturalWidth) return;
            const rect = zoomImg.getBoundingClientRect();
            const scale = Math.min(rect.width / zoomImg.naturalWidth, rect.height / zoomImg.naturalHeight);
            const w = zoomImg.naturalWidth * scale;
            const h = zoomImg.naturalHeight * scale;
            const x0 = rect.left + (rect.width - w) / 2;
            const y0 = rect.top + (rect.height - h) / 2;
            const onPhoto = event.clientX >= x0 && event.clientX <= x0 + w
              && event.clientY >= y0 && event.clientY <= y0 + h;
            if (!onPhoto) this.zoom.close();
            return;
          }
          // Click on the empty stage (not the image or a control) closes.
          if (
            event.target === this.zoom
            || event.target.classList.contains('gal-zoom__stage')
            || event.target.hasAttribute('data-zoom-item')
          ) {
            this.zoom.close();
          }
        });
        this.zoom?.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.stepZoom(-1);
          if (event.key === 'ArrowRight') this.stepZoom(1);
        });
        // Swipe between images inside the lightbox.
        let touchX = null;
        this.zoom?.addEventListener('touchstart', (event) => { touchX = event.touches[0].clientX; }, { passive: true });
        this.zoom?.addEventListener('touchend', (event) => {
          if (touchX === null) return;
          const dx = event.changedTouches[0].clientX - touchX;
          if (Math.abs(dx) > 48) this.stepZoom(dx < 0 ? 1 : -1);
          touchX = null;
        }, { passive: true });
        // Switch to the variant image when the variant changes. The
        // .shopify-section wrapper survives editor reloads, so the
        // listener must be removed on disconnect or it piles up.
        this.variantTarget = this.closest('section, .shopify-section');
        this.onVariantChange = (event) => {
          const mediaId = event.detail.variant?.media_id;
          if (mediaId && !event.detail.initial) this.show(String(mediaId));
        };
        this.variantTarget?.addEventListener('variant:change', this.onVariantChange);
      }

      disconnectedCallback() {
        this.variantTarget?.removeEventListener('variant:change', this.onVariantChange);
      }

      show(mediaId) {
        this.querySelectorAll('[data-gallery-item]').forEach((item) => {
          item.hidden = item.dataset.galleryItem !== mediaId;
        });
        this.querySelectorAll('button[data-media-id]').forEach((thumb) => {
          thumb.classList.toggle('on', thumb.dataset.mediaId === mediaId);
          thumb.setAttribute('aria-current', thumb.dataset.mediaId === mediaId);
        });
      }

      openZoom(mediaId) {
        if (!this.zoom) return;
        this.showZoom(mediaId);
        this.zoom.showModal();
      }

      showZoom(mediaId) {
        const items = this.zoom.querySelectorAll('[data-zoom-item]');
        let found = false;
        items.forEach((item) => {
          const on = item.dataset.zoomItem === mediaId;
          item.hidden = !on;
          if (on) found = true;
        });
        if (!found && items.length) {
          items[0].hidden = false;
          mediaId = items[0].dataset.zoomItem;
        }
        this.zoom.querySelectorAll('button[data-zoom-thumb]').forEach((thumb) => {
          thumb.classList.toggle('on', thumb.dataset.zoomThumb === mediaId);
        });
        // Keep the underlying gallery in sync so closing lands on the same image.
        this.show(mediaId);
      }

      stepZoom(direction) {
        const items = [...this.zoom.querySelectorAll('[data-zoom-item]')];
        if (items.length < 2) return;
        const index = items.findIndex((item) => !item.hidden);
        const next = items[(index + direction + items.length) % items.length];
        this.showZoom(next.dataset.zoomItem);
      }
    }
  );

  /* -------------------------------------------- <sl-sticky-atc> */
  customElements.define(
    'sl-sticky-atc',
    class extends HTMLElement {
      connectedCallback() {
        const anchorSelector = this.dataset.anchor;
        const anchor = anchorSelector && document.querySelector(anchorSelector);
        if (!anchor) return;
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.classList.toggle('on', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          },
          { threshold: 0 }
        );
        this.observer.observe(anchor);
        this.querySelector('[data-sticky-atc]')?.addEventListener('click', () => {
          const form = document.querySelector('sl-product-form form[data-type="add-to-cart-form"]');
          form?.requestSubmit();
        });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    }
  );

  /* -------------------------------------------------- <sl-cart> */
  customElements.define(
    'sl-cart',
    class extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.addEventListener('change', (event) => {
          const input = event.target.closest('input[data-line]');
          if (input) this.changeLine(input.dataset.line, parseInt(input.value, 10) || 0, input.dataset.key);
        });
        this.addEventListener('click', (event) => {
          const remove = event.target.closest('[data-remove-line]');
          if (remove) {
            event.preventDefault();
            this.changeLine(remove.dataset.removeLine, 0, remove.dataset.key);
          }
        });
        this.querySelector('form[data-discount-form]')?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.applyDiscount(event.target);
        });
        // Re-render when the cart changes outside this section (e.g. a
        // quick-add from the upsell rows on the cart page).
        this.onCartUpdated = () => {
          if (!this.internalUpdate) this.refresh();
        };
        document.addEventListener('cart:updated', this.onCartUpdated);
      }

      disconnectedCallback() {
        document.removeEventListener('cart:updated', this.onCartUpdated);
      }

      async refresh() {
        try {
          const url = new URL(location.href);
          url.searchParams.set('section_id', this.sectionId);
          const response = await fetch(url);
          if (!response.ok) return;
          const fresh = parseHTML(await response.text()).querySelector('sl-cart');
          if (fresh) this.replaceWith(fresh);
        } catch {
          /* leave the current view in place */
        }
      }

      setBusy(busy) {
        this.classList.toggle('is-busy', busy);
        this.setAttribute('aria-busy', busy);
      }

      async changeLine(line, quantity, key) {
        this.setBusy(true);
        this.internalUpdate = true;
        try {
          const data = await SLCart.change({
            id: key || undefined,
            line: key ? undefined : parseInt(line, 10),
            quantity,
            sections: this.sectionId,
          });
          this.renderSection(data.sections?.[this.sectionId]);
        } catch (error) {
          toast(error.message);
          // A rejected quantity (422, e.g. more than in stock) must not stay
          // in the input — re-render from the real cart state.
          await this.refresh();
        } finally {
          this.internalUpdate = false;
          this.setBusy(false);
        }
      }

      async applyDiscount(form) {
        const input = form.querySelector('input[name="discount"]');
        const code = input?.value.trim();
        if (!code) return;
        this.setBusy(true);
        this.internalUpdate = true;
        try {
          const existing = (this.dataset.discounts || '').split(',').filter(Boolean);
          const data = await SLCart.update({
            discount: [...existing, code].join(','),
            sections: this.sectionId,
          });
          const applied = (data.discount_codes || []).some(
            (entry) => entry.code.toLowerCase() === code.toLowerCase() && entry.applicable
          );
          this.renderSection(data.sections?.[this.sectionId]);
          if (!applied) toast(strings.discount_invalid);
        } catch (error) {
          toast(error.message);
        } finally {
          this.internalUpdate = false;
          this.setBusy(false);
        }
      }

      renderSection(html) {
        if (!html) {
          location.reload();
          return;
        }
        const doc = parseHTML(html);
        const fresh = doc.querySelector('sl-cart');
        if (fresh) this.replaceWith(fresh);
      }
    }
  );

  /* Remove an applied discount code (rendered as a chip in the summary).
     The cart section re-renders itself via the cart:updated event. */
  document.addEventListener('click', async (event) => {
    const chip = event.target.closest('[data-remove-discount]');
    if (!chip) return;
    event.preventDefault();
    const cartEl = chip.closest('sl-cart');
    const remaining = (cartEl?.dataset.discounts || '')
      .split(',')
      .filter((code) => code && code.toLowerCase() !== chip.dataset.removeDiscount.toLowerCase());
    try {
      await SLCart.update({ discount: remaining.join(',') });
    } catch (error) {
      toast(error.message);
    }
  });

  /* Auto-submit forms (e.g. the sort select on the shop overview). */
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-autosubmit]')) {
      event.target.closest('form')?.submit();
    }
  });

  /* --------------------------------------------- quick add (tiles) */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('button[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    button.disabled = true;
    try {
      await SLCart.add([{ id: parseInt(button.dataset.quickAdd, 10), quantity: 1 }]);
      button.classList.add('done');
      if (button.dataset.quickAddSwap !== 'false') {
        const label = button.querySelector('[data-label]') || button;
        label.textContent = strings.added_to_cart_short;
      }
      toast(strings.added_to_cart);
      setTimeout(() => {
        button.disabled = false;
      }, 1200);
    } catch (error) {
      button.disabled = false;
      toast(error.message);
    }
  });

  /* -------------------------------------------- <sl-filter-rail> */
  customElements.define(
    'sl-collection',
    class extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.setupDescClamp();

        // View toggle (large/compact) persisted per browser.
        const storedView = storage.get('sl-collection-view');
        if (storedView) this.applyView(storedView);
        this.addEventListener('click', (event) => {
          const viewButton = event.target.closest('button[data-view-toggle]');
          if (viewButton) {
            this.applyView(viewButton.dataset.viewToggle);
            storage.set('sl-collection-view', viewButton.dataset.viewToggle);
            return;
          }
          const filterLink = event.target.closest('a[data-filter-link]');
          if (filterLink) {
            // Leave modifier/middle clicks to the browser (open in new tab).
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
            event.preventDefault();
            this.load(filterLink.href, true);
          }
        });

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-sort-select]')) {
            const url = new URL(location.href);
            url.searchParams.set('sort_by', event.target.value);
            this.load(url.href, true);
          }
          if (event.target.matches('[data-price-input]')) {
            const form = event.target.closest('form');
            if (form) {
              // Merge the price params into the current URL so active
              // list filters and sort order are preserved.
              const url = new URL(location.href);
              [...form.querySelectorAll('[data-price-input]')].forEach((input) => {
                if (input.value) url.searchParams.set(input.name, input.value);
                else url.searchParams.delete(input.name);
              });
              this.load(url.href, true);
            }
          }
        });
        this.onPopState = () => this.load(location.href, false);
        addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        removeEventListener('popstate', this.onPopState);
        this.controller?.abort();
      }

      applyView(view) {
        this.querySelectorAll('[data-view-root]').forEach((root) => (root.dataset.view = view));
        this.querySelectorAll('button[data-view-toggle]').forEach((button) => {
          button.classList.toggle('on', button.dataset.viewToggle === view);
          button.setAttribute('aria-pressed', button.dataset.viewToggle === view);
        });
      }

      /* Clamp the SEO description to two lines and offer an expand toggle
         only when the text actually overflows. Without JS the full text
         stays visible — identical content for crawlers either way. */
      setupDescClamp() {
        const desc = this.querySelector('[data-desc-clamp]');
        const toggle = this.querySelector('[data-desc-toggle]');
        if (!desc || !toggle) return;
        const labels = [toggle.textContent.trim(), toggle.dataset.lessLabel || toggle.textContent.trim()];
        desc.classList.add('is-clamped');
        if (desc.scrollHeight <= desc.clientHeight + 2) {
          desc.classList.remove('is-clamped');
          return;
        }
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          const expanded = desc.classList.toggle('is-clamped') === false;
          toggle.setAttribute('aria-expanded', expanded);
          toggle.textContent = expanded ? labels[1] : labels[0];
        });
      }

      async load(url, push) {
        // Only the latest request may render: rapid filter clicks would
        // otherwise resolve out of order and show a stale result set.
        this.controller?.abort();
        const controller = new AbortController();
        this.controller = controller;
        this.classList.add('is-busy');
        try {
          const fetchUrl = new URL(url, location.origin);
          fetchUrl.searchParams.set('section_id', this.sectionId);
          const response = await fetch(fetchUrl, { signal: controller.signal });
          if (!response.ok) throw new Error();
          const doc = parseHTML(await response.text());
          const fresh = doc.querySelector('sl-collection');
          if (fresh) {
            const view = storage.get('sl-collection-view');
            this.innerHTML = fresh.innerHTML;
            if (view) this.applyView(view);
            initMotion(this);
            this.setupDescClamp();
          }
          if (push) history.pushState({}, '', url);
        } catch (error) {
          if (error?.name === 'AbortError') return;
          location.href = url;
        } finally {
          if (this.controller === controller) this.classList.remove('is-busy');
        }
      }
    }
  );

  /* ---------------------------------------- <sl-recommendations> */
  customElements.define(
    'sl-recommendations',
    class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded) return;
        // A hidden element never intersects (display: none), so the
        // initial server-rendered empty state must fetch right away.
        if (this.hidden) {
          this.load();
          return;
        }
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              this.observer.disconnect();
              this.load();
            }
          },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async load() {
        const { productId, sectionId, intent } = this.dataset;
        if (!productId || !sectionId) return;
        let loaded = await this.fetchRecommendations(intent || 'related');
        // Complementary pairs are merchant-curated and often empty; fall
        // back to metadata-based related products so the block still fills.
        if (!loaded && intent === 'complementary') {
          loaded = await this.fetchRecommendations('related');
        }
        this.hidden = !loaded;
      }

      async fetchRecommendations(intent) {
        const { productId, sectionId, limit } = this.dataset;
        try {
          const url = `${routes.product_recommendations_url}?product_id=${productId}&limit=${limit || 3}&intent=${intent}&section_id=${sectionId}`;
          const response = await fetch(url);
          if (!response.ok) return false;
          const doc = parseHTML(await response.text());
          const fresh = doc.querySelector('sl-recommendations');
          if (fresh && fresh.querySelector('[data-rec-item]')) {
            this.dataset.loaded = 'true';
            this.innerHTML = fresh.innerHTML;
            this.hidden = false;
            initMotion(this);
            return true;
          }
          return false;
        } catch {
          return false;
        }
      }
    }
  );

  /* -------------------------------------------- <sl-blog-filter> */
  customElements.define(
    'sl-blog-filter',
    class extends HTMLElement {
      connectedCallback() {
        this.chips = [...this.querySelectorAll('[data-blog-chip]')];
        this.items = [...this.querySelectorAll('[data-blog-item]')];
        this.hits = this.querySelector('[data-blog-hits]');
        this.resetBtn = this.querySelector('[data-blog-reset]');
        this.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-blog-chip]');
          if (chip) {
            chip.setAttribute('aria-pressed', chip.getAttribute('aria-pressed') !== 'true');
            this.apply();
            return;
          }
          if (event.target.closest('[data-blog-reset]')) {
            this.chips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
            this.apply();
          }
        });
      }

      apply() {
        const active = this.chips
          .filter((chip) => chip.getAttribute('aria-pressed') === 'true')
          .map((chip) => chip.dataset.blogChip);
        let visible = 0;
        this.items.forEach((item) => {
          const show = active.length === 0 || active.includes(item.dataset.blogItem);
          item.hidden = !show;
          if (show) visible++;
        });
        if (this.hits) {
          this.hits.textContent = strings.hits.replace('[count]', visible).replace('[total]', this.items.length);
        }
        if (this.resetBtn) this.resetBtn.hidden = active.length === 0;
      }
    }
  );

  /* ------------------------------------------------- <sl-toc> */
  customElements.define(
    'sl-toc',
    class extends HTMLElement {
      connectedCallback() {
        const target = document.querySelector(this.dataset.target);
        if (!target) {
          this.hidden = true;
          return;
        }
        const headings = [...target.querySelectorAll('h2')];
        if (headings.length < 2) {
          this.hidden = true;
          return;
        }
        const nav = this.querySelector('nav') || this;
        // Re-connects (editor reorder) must not append the links twice.
        nav.querySelectorAll('a').forEach((link) => link.remove());
        headings.forEach((heading, index) => {
          if (!heading.id) heading.id = 'abschnitt-' + (index + 1);
          const link = document.createElement('a');
          link.href = '#' + heading.id;
          link.textContent = heading.textContent;
          nav.appendChild(link);
        });
        const links = [...nav.querySelectorAll('a')];
        this.spy = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              links.forEach((link, index) => link.classList.toggle('on', headings[index] === entry.target));
            });
          },
          { rootMargin: '-110px 0px -70% 0px' }
        );
        headings.forEach((heading) => this.spy.observe(heading));
        links[0]?.classList.add('on');
      }

      disconnectedCallback() {
        this.spy?.disconnect();
      }
    }
  );

  /* -------------------------------------------- <sl-progress> */
  customElements.define(
    'sl-progress',
    class extends HTMLElement {
      connectedCallback() {
        this.target = document.querySelector(this.dataset.target);
        if (!this.target) return;
        this.onScroll = () => {
          const rect = this.target.getBoundingClientRect();
          const done = Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height - innerHeight * 0.9)));
          this.style.width = (done * 100).toFixed(1) + '%';
        };
        addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        removeEventListener('scroll', this.onScroll);
      }
    }
  );

  /* ---------------------------------------------- <sl-share> */
  customElements.define(
    'sl-share',
    class extends HTMLElement {
      connectedCallback() {
        this.querySelector('[data-share-copy]')?.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(location.href.split('#')[0]);
            toast(strings.link_copied);
          } catch {
            /* clipboard unavailable */
          }
        });
        this.querySelector('[data-share-print]')?.addEventListener('click', () => print());
      }
    }
  );

  /* ------------------------------------------ <sl-lazy-video> */
  /* Decorative looping video over a poster image. Sources stay in
     data-src until the page has loaded, so the video never competes with
     the LCP; nothing loads with reduced motion or while the element is
     hidden (e.g. hero media switched off on mobile). */
  customElements.define(
    'sl-lazy-video',
    class extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video || reducedMotion.matches || !this.getClientRects().length) return;
        this.start = () => {
          this.video.querySelectorAll('source[data-src]').forEach((source) => {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
          });
          this.video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
          this.video.load();
          this.observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) this.video.play().catch(() => {});
            else this.video.pause();
          });
          this.observer.observe(this);
        };
        if (document.readyState === 'complete') this.start();
        else addEventListener('load', this.start, { once: true });
      }

      disconnectedCallback() {
        removeEventListener('load', this.start);
        this.observer?.disconnect();
      }
    }
  );
})();
